import { useState } from "react";
import type { Bootstrap, BudgetItem } from "../../shared/types";
import { api, type BudgetInput } from "../lib/api";
import { parseMoney } from "../lib/format";
import Modal from "./Modal";
import Select from "./Select";
import { useConfirm } from "./ConfirmProvider";

// Add or edit a budget bucket (planned spend). The amount is per cadence unit:
// weekly = per week (scaled to the cycle), monthly = per cycle, once = this cycle.
export default function BudgetModal({
  data,
  item,
  onClose,
  onSaved,
}: {
  data: Bootstrap;
  item: BudgetItem | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const editing = item != null;
  const [name, setName] = useState(item?.name ?? "");
  const [amount, setAmount] = useState(item ? (item.amountMinor / 100).toFixed(2) : "");
  const [cadence, setCadence] = useState<string>(item?.cadence ?? "monthly");
  const [categoryId, setCategoryId] = useState(item?.categoryId ? String(item.categoryId) : "");
  const [paidBy, setPaidBy] = useState(item?.paidBy ? String(item.paidBy) : "");
  const [notes, setNotes] = useState(item?.notes ?? "");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const ask = useConfirm();

  async function save() {
    const minor = parseMoney(amount);
    if (!name.trim()) return setError("Give the budget a name");
    if (minor == null || minor <= 0) return setError("Enter a valid amount");
    const payload: BudgetInput = {
      name: name.trim(),
      amountMinor: minor,
      cadence: cadence as BudgetInput["cadence"],
      categoryId: categoryId ? Number(categoryId) : null,
      paidBy: paidBy ? Number(paidBy) : null,
      notes: notes.trim() || null,
    };
    setSaving(true);
    try {
      if (editing) await api.updateBudget(item.id, payload);
      else await api.createBudget(payload);
      onSaved();
    } catch (e) {
      setError((e as Error).message);
      setSaving(false);
    }
  }

  async function remove() {
    if (!item) return;
    if (!(await ask({ title: "Delete budget?", message: `Delete budget "${item.name}"? Spends already logged against it stay as normal spends.`, confirmLabel: "Delete budget", danger: true }))) return;
    setSaving(true);
    try {
      await api.deleteBudget(item.id);
      onSaved();
    } catch (e) {
      setError((e as Error).message);
      setSaving(false);
    }
  }

  return (
    <Modal title={editing ? "Edit budget" : "Add budget"} onClose={onClose}>
      <div className="form">
        <label className="field">
          <span className="field__label">Name</span>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Weekly shop" autoFocus />
        </label>
        <div className="form__row">
          <label className="field">
            <span className="field__label">Amount (£)</span>
            <input className="input" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="100.00" />
          </label>
          <div className="field">
            <span className="field__label">How often</span>
            <Select
              value={cadence}
              onChange={setCadence}
              ariaLabel="How often"
              options={[
                { value: "weekly", label: "Per week" },
                { value: "monthly", label: "Per month" },
                { value: "once", label: "One-off (this month)" },
              ]}
            />
          </div>
        </div>
        <p className="field__hint">
          {cadence === "weekly"
            ? "A weekly amount — multiplied by the weeks in your pay-cycle."
            : cadence === "once"
              ? "A one-off amount for this cycle only."
              : "A set amount for the whole cycle."}
        </p>
        <div className="form__row">
          <div className="field">
            <span className="field__label">Category (optional)</span>
            <Select
              value={categoryId}
              onChange={setCategoryId}
              placeholder="—"
              ariaLabel="Category"
              options={[{ value: "", label: "—" }, ...data.categories.map((c) => ({ value: String(c.id), label: c.name }))]}
            />
          </div>
          <div className="field">
            <span className="field__label">Who's spending it (optional)</span>
            <Select
              value={paidBy}
              onChange={setPaidBy}
              placeholder="Either / both"
              ariaLabel="Who's spending it"
              options={[{ value: "", label: "Either / both" }, ...data.household.members.map((m) => ({ value: String(m.id), label: m.name }))]}
            />
          </div>
        </div>
        <label className="field">
          <span className="field__label">Notes (optional)</span>
          <textarea className="input" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Anything to remember about this budget" />
        </label>
        {error && <p className="form__error">{error}</p>}
        <div className="form__actions form__actions--split">
          {editing ? <button className="btn btn--danger" onClick={remove} disabled={saving}>Delete</button> : <span />}
          <div className="form__actions">
            <button className="btn btn--ghost" onClick={onClose}>Cancel</button>
            <button className="btn btn--primary" onClick={save} disabled={saving}>
              {saving ? "Saving…" : editing ? "Save" : "Add budget"}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
